import { Button, Stack } from "@mui/material";
import { useState, forwardRef, useEffect } from "react";
import Modal, { CommonModalProps } from "..";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import ModalCreateShareStepThree from "./ModalCreateShareSteps/modal-create-share-step-three";
import ModalCreateShareStepTwo from "./ModalCreateShareSteps/modal-create-share-step-two";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import ModalCreateShareStepFour from "./ModalCreateShareSteps/modal-create-share-step-four";
import TitleCollectionCard from "../../Cards/TitleCollectionCard/title-collection-card";
import { assetService, cacheService, shareService, thumbnailService } from "../../../Core/Api/services";
import { WatermarkTemplates } from "../../../Core/Api/Types/watermark-templates";
import { MailingListDto } from "../../../Core/Api/Types/get-mailing-list-response";
import ModalCreateShareStepOne from "./ModalCreateShareSteps/modal-create-share-step-one";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { AudioLanguageSettings } from "../../../Core/Api/Types/audio-language-settings";
import { SubtitleLanguageSettings } from "../../../Core/Api/Types/subtitle-language-settings";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { AudioLanguageSettingsWithLabel } from "../../../Core/Api/Types/audio-language-settings-with-label";
import { SubtitleLanguageSettingsWithLabel } from "../../../Core/Api/Types/subtitle-language-settings-with-label";
import { GetGraphResponse } from "../../../Core/Api/Types/get-graph-response";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { AudioType } from "../../../Core/Api/Types/audio-type";
import { ShareMediaType } from "../../../Core/Api/Types/share-media-type";
import { AudioMixVersion } from "../../../Core/Api/Types/audio-mix-version";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { UserHasAcl } from "../../../Utils/helpers";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import { TranskodeAudioTrackChannelPosition } from "../../../Core/Api/Types/transkode-audio-track-channel-position";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { useTranslation } from "react-i18next";

export interface ShareFeature {
  id?: number;
  name?: string | null;
  episodes: Episode[];
  epList: number[];
}

export interface Episode {
  nb: number;
  audioLanguageOptions: AudioLanguageSettingsWithLabel[];
  subtitleLanguageOptions: SubtitleLanguageSettingsWithLabel[];
}

export interface ShareStepOne {
  objectId?: number | null;
  titleId?: number | null;
  objectInfos: DbAssetObjectInfos | null;
  objectName?: string | null;
  objectLayer?: ObjectLayer | null;
  objectClass?: ObjectClass | null;
  features?: ShareFeature[] | null;
  isSeason: boolean;
}

export interface ShareStepTwo {
  feature: ShareFeature | null;
  epSelected: number[];
}

export interface ShareLocalizationSection {
  localizationAudioSelected: AudioLanguageSettingsWithLabel[];
  localizationSubtitlesSelected: SubtitleLanguageSettingsWithLabel[];
  audioLanguageOptions: AudioLanguageSettingsWithLabel[];
  subtitleLanguageOptions: SubtitleLanguageSettingsWithLabel[];
  audioFinal: AudioLanguageSettings[];
  subtitleFinal: SubtitleLanguageSettings[];
  isValid: boolean;
}

export interface ShareSettingsSection {
  shareName: string | null;
  expirationDate: number | null;
  comment: string | null;
  maxViews: number | null;
}

export interface ShareSecuritySection {
  maxIps: number | null;
  maxClips: number | null;
  maxClipDuration: string | null;
  watermarkTemplate: WatermarkTemplates | null;
  watermarkContent: string | null;
  printTimeCode: boolean;
  isValid: boolean;
  securityMode: ShareMediaType | null;
}

export interface ShareRecipientsSection {
  emailList: MailingListDto[];
  emails: string[];
}

export interface ShareFormValues {
  isFormValid: boolean;
  stepOne: ShareStepOne;
  stepTwo: ShareStepTwo;
  isAsset: boolean;
  assetIds: number[];
  securitySection: ShareSecuritySection;
  localizationSection: ShareLocalizationSection;
  settingsSection: ShareSettingsSection;
  recipientsSection: ShareRecipientsSection;
  preselectedContentId?: number;
  onTheFlyEmailOverlay: boolean;
  isIndependentItems?: boolean;
}

type CloseClbProps = {
  message: string;
};

export type ModalCreateShareElementProps = {
  object?: DbAssetObjectInfos;
  assetIds?: number[];
  preselectedContentId?: number;
  isIndependentItems?: boolean;
  onClose?: (props: CloseClbProps) => void;
};

export type ModalCreateShareProps = CommonModalProps<ModalCreateShareElementProps>;

const ModalCreateShare = forwardRef<any, ModalCreateShareProps>((props, ref) => {
  const { t } = useTranslation();
  const startingStep = props.assetIds ? 3 : props.object ? (props.object.layer === ObjectLayer.Asset ? 3 : 2) : 1;
  const [activeStep, setActiveStep] = useState(startingStep);
  const [securityText, setSecurityText] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [formValues, setFormValues] = useState<ShareFormValues>({
    stepOne: {
      isSeason: props.object ? GetMetadataByTagName(props.object, "titleType")?.value !== "Movie" : false,
      features: props.object?.childObjects?.map((element: DbAssetObjectInfos) => {
        return {
          id: element.id,
          name: element.name,
          episodes: [],
          epList: [],
        };
      }),
      objectId: props.object?.id,
      titleId: props.object?.parentTitleId,
      objectInfos: props.object ?? null,
      objectLayer: props.object?.layer,
      objectClass: props.object?.classification,
      objectName: props.object?.name,
    },
    stepTwo: {
      feature: null,
      epSelected: [],
    },
    localizationSection: {
      audioLanguageOptions: [],
      localizationAudioSelected: [],
      localizationSubtitlesSelected: [],
      subtitleLanguageOptions: [],
      audioFinal: [],
      subtitleFinal: [],
      isValid: false,
    },
    recipientsSection: {
      emailList: [],
      emails: [],
    },
    settingsSection: {
      comment: null,
      expirationDate: null,
      maxViews: null,
      shareName: null,
    },
    securitySection: {
      maxClipDuration: null,
      maxClips: null,
      maxIps: null,
      printTimeCode: false,
      watermarkContent: null,
      watermarkTemplate: null,
      isValid: false,
      securityMode: null,
    },
    isFormValid: false,
    isAsset: props.assetIds !== undefined,
    assetIds: props.assetIds || [],
    preselectedContentId: props.preselectedContentId,
    onTheFlyEmailOverlay: false,
    isIndependentItems: props.isIndependentItems ?? false,
  });

  const [localizationSection, setLocalizationSection] = useState<ShareLocalizationSection>({
    audioLanguageOptions: [],
    localizationAudioSelected: [],
    localizationSubtitlesSelected: [],
    subtitleLanguageOptions: [],
    audioFinal: [],
    subtitleFinal: [],
    isValid: false,
  });

  const [settingsSection, setSettingsSection] = useState<ShareSettingsSection>({
    comment: null,
    expirationDate: cacheService.GetGroup()!.defaultShareViewDays
      ? cacheService.GetGroup()!.defaultShareViewDays
      : cacheService.GetPlatformSettings()?.defaultShareViewDays ?? null,
    maxViews: cacheService.GetGroup()!.defaultShareViews
      ? cacheService.GetGroup()!.defaultShareViews
      : cacheService.GetPlatformSettings()?.defaultShareViews ?? null,
    shareName: null,
  });

  const securityModes = UserHasAcl(AclTypes.aclFastModeShare)
    ? [ShareMediaType.StreamingBasic, ShareMediaType.StreamingTagSecure]
    : [ShareMediaType.StreamingTagSecure];

  const [securitySection, setSecuritySection] = useState<ShareSecuritySection>({
    maxClipDuration: cacheService.GetGroup()!.defaultShareViewClipsDuration
      ? cacheService.GetGroup()!.defaultShareViewClipsDuration
      : cacheService.GetPlatformSettings()?.defaultShareViewClipsDuration ?? null,
    maxClips: cacheService.GetGroup()!.defaultShareViewClipsNumber
      ? cacheService.GetGroup()!.defaultShareViewClipsNumber
      : cacheService.GetPlatformSettings()?.defaultShareViewClipsNumber ?? null,
    maxIps: cacheService.GetGroup()!.defaultShareViewIPs
      ? cacheService.GetGroup()!.defaultShareViewIPs
      : cacheService.GetPlatformSettings()?.defaultShareViewIPs ?? null,
    printTimeCode: false,
    watermarkContent: null,
    watermarkTemplate: WatermarkTemplates.Standard_Duplicated,
    isValid: false,
    securityMode:
      securityModes.includes(ShareMediaType.StreamingBasic) &&
      cacheService.GetPlatformMode() !== PlatformMode.Distribution
        ? ShareMediaType.StreamingBasic
        : ShareMediaType.StreamingTagSecure,
  });

  const [recipientsSection, setRecipientsSection] = useState<ShareRecipientsSection>({
    emailList: [],
    emails: [],
  });

  const { closeModal } = props;
  const enqueueCustomSnackbar = useCustomSnackbar();

  const metadata: DbMetadataFieldInfos[] = cacheService.GetMetadata();

  useEffect(() => {
    setIsLoading(!isLoading);
  }, [activeStep]);

  const onClickNext = () => {
    setIsLoading(true);
    if (activeStep === 1 && formValues.stepOne.objectLayer === ObjectLayer.Asset) {
      // If we select asset, no need to pass through step 2
      setActiveStep((prevActiveStep) => prevActiveStep + 2);
      return;
    } else if (activeStep === 2 && formValues.stepTwo.feature?.id) {
      assetService.GetObjectById(formValues.stepTwo.feature?.id).then(function (response: GetGraphResponse) {
        if (!response.graph) return;
        computeLangOptionsAvailable(response?.graph, formValues.stepTwo.epSelected.length === 0);

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        return;
      });
    } else if (activeStep === 3) {
      setFormValues((prevState) => ({
        ...prevState,
        securitySection: securitySection,
        settingsSection: settingsSection,
        recipientsSection: recipientsSection,
        localizationSection: {
          ...prevState.localizationSection,
          localizationAudioSelected: localizationSection.localizationAudioSelected,
          audioFinal: localizationSection.localizationAudioSelected.map((element) => {
            return { audioType: element.audioType, audioVersion: element.audioVersion, value: element.value };
          }),
          localizationSubtitlesSelected: localizationSection.localizationSubtitlesSelected,
          subtitleFinal: localizationSection.localizationSubtitlesSelected.map((element) => {
            return { subtitleType: element.subtitleType, value: element.value };
          }),
          settingsSection: settingsSection,
          recipientsSection: recipientsSection,
        },
      }));
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const computeLangOptionsAvailable = (object: DbAssetObjectInfos, isSeason: boolean) => {
    let audioOptions: AudioLanguageSettingsWithLabel[] = [];
    let subtitleOptions: SubtitleLanguageSettingsWithLabel[] = [];

    let fieldAudio = metadata.find((x) => x.tagName === "languageAudio");
    let fieldSubtitle = metadata.find((x) => x.tagName === "languageSubtitle");

    object.childObjects
      ?.filter((x) => x.classification === ObjectClass.Mezz && x.status === ObjectStatus.Accepted)
      .forEach((asset: DbAssetObjectInfos) => {
        let channels = GetMetadataByTagName(asset, "audioChannels")?.value;

        // --------------
        // Audio
        // --------------
        if (channels) {
          channels = channels.filter((x) => x.channelPosition === TranskodeAudioTrackChannelPosition.Lt); // Consider we take only 2.0 mixes

          for (let i = 0; i < channels.length; i++) {
            let audioType: AudioType = AudioType.Undefined;
            let audioVersion: AudioMixVersion = channels[i].version as AudioMixVersion;

            switch (audioVersion) {
              case AudioMixVersion.MusicAndEffects:
                audioType = AudioType.AudioMusicAndEffects;
                break;
              case AudioMixVersion.Dubbed:
              case AudioMixVersion.OV:
                audioType = AudioType.AudioMixCouple;
                break;
              case AudioMixVersion.Commentary:
                audioType = AudioType.Commentary;
                break;
              case AudioMixVersion.AD:
                audioType = AudioType.AudioDescription;
                break;
            }

            let audio: AudioLanguageSettingsWithLabel = {
              audioType: audioType,
              value: audioVersion === AudioMixVersion.MusicAndEffects ? "M&E" : channels[i].language?.value,
              label:
                audioVersion === AudioMixVersion.MusicAndEffects
                  ? "M&E"
                  : fieldAudio?.values?.find((x) => x.value === channels[i].language?.value)?.label +
                    " (" +
                    channels[i].language?.value +
                    ")",
              audioVersion: audioVersion,
            };

            if (
              !audioOptions.some(
                (element) =>
                  element.audioType === audio.audioType &&
                  element.value === audio.value &&
                  element.audioVersion === audio.audioVersion
              )
            )
              audioOptions.push(audio);
          }
        }

        // --------------
        // Subtitle
        // --------------
        if (asset.type === ObjectType.Subtitle) {
          let metaLangSubtitle = GetMetadataByTagName(asset, "languageSubtitle");
          let labelSubtitle = fieldSubtitle?.values?.find((x) => x.value === metaLangSubtitle?.value.value)?.label;

          let subtitle: SubtitleLanguageSettingsWithLabel = {
            subtitleType: GetMetadataByTagName(asset, "subtitleType")?.value,
            value: metaLangSubtitle?.value.value,
            label: (labelSubtitle ? labelSubtitle : "-") + " (" + metaLangSubtitle?.value.value + ")",
          };

          if (
            subtitle.value &&
            !subtitleOptions.some(
              (element) => element.subtitleType === subtitle.subtitleType && element.value === subtitle.value
            )
          )
            subtitleOptions.push(subtitle);
        }
      });

    setLocalizationSection((prevState) => ({
      ...prevState,
      audioLanguageOptions: audioOptions,
      subtitleLanguageOptions: subtitleOptions,
    }));

    if (isSeason) computeLangOptionsAvailableSeason(object, audioOptions, subtitleOptions);
  };

  const computeLangOptionsAvailableSeason = (
    object: DbAssetObjectInfos,
    audioOptions: AudioLanguageSettingsWithLabel[],
    subtitleOptions: SubtitleLanguageSettingsWithLabel[]
  ) => {
    // Filter audio and subtitles that are not in all episodes
    let epAssets: DbAssetObjectInfos[] = [];

    audioOptions.forEach((audio) => {
      formValues.stepTwo.epSelected.forEach((epNb) => {
        epAssets = object.childObjects?.filter(
          (object: DbAssetObjectInfos) => GetMetadataByTagName(object, "serieEpisodeNumberProduction")?.value === epNb
        )!;
        let audioPresent: boolean = false;
        epAssets.every((element) => {
          let audioAsset: AudioLanguageSettings = {
            audioType: GetMetadataByTagName(element, "audioOverallVersion")?.value,
            value: GetMetadataByTagName(element, "languageAudio")?.value.value,
            audioVersion: null,
          };

          if (audioAsset.audioType === audio.audioType && audioAsset.value === audio.value) {
            audioPresent = true;
            return true;
          }

          return false;
        });
        if (!audioPresent) audioOptions.filter((element) => element !== audio);
      });
    });

    subtitleOptions.forEach((subtitle) => {
      formValues.stepTwo.epSelected.forEach((epNb) => {
        epAssets = object.childObjects?.filter(
          (object: DbAssetObjectInfos) => GetMetadataByTagName(object, "serieEpisodeNumberProduction")?.value === epNb
        )!;
        let subtitlePresent: boolean = false;
        epAssets.every((element) => {
          let subtitleAsset: SubtitleLanguageSettings = {
            subtitleType: GetMetadataByTagName(element, "subtitleType")?.value,
            value: GetMetadataByTagName(element, "languageSubtitle")?.value.value,
          };

          if (subtitleAsset.subtitleType === subtitle.subtitleType && subtitleAsset.value === subtitle.value) {
            subtitlePresent = true;
            return true;
          }

          return false;
        });
        if (!subtitlePresent) subtitleOptions.filter((element) => element !== subtitle);
      });
    });

    setLocalizationSection((prevState) => ({
      ...prevState,
      audioLanguageOptions: audioOptions,
      subtitleLanguageOptions: subtitleOptions,
    }));
  };

  const onClickBack = () => {
    setIsLoading(true);
    setActiveStep((prevActiveStep) =>
      activeStep === 3 && formValues.stepOne.objectLayer === ObjectLayer.Asset ? prevActiveStep - 2 : prevActiveStep - 1
    );
  };

  const onClickCreate = () => {
    setIsLoading(true);

    if (formValues.assetIds.length > 0) {
      shareService
        .CreateMultipleShare(formValues)
        .then((response) => {
          if (response.success) {
            enqueueCustomSnackbar(t("alert:success.newShareCreated"), "success");
            closeModal();
          } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");

          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    } else {
      shareService
        .CreateShare(formValues)
        .then((response) => {
          if (response.success) {
            enqueueCustomSnackbar(t("alert:success.newShareCreated"), "success");
            closeModal();
          } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");

          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  };

  return (
    <Modal
      title={t("modal:title.createNewShare")}
      reference={ref}
      closeModal={closeModal}
      width={700}
      footer={
        <Stack
          direction="row"
          justifyContent={activeStep !== 1 && activeStep > startingStep ? "space-between" : "flex-end"}
        >
          {activeStep !== 1 && activeStep > startingStep && (
            <Button onClick={onClickBack} disabled={isLoading}>
              {t("common:back")}
            </Button>
          )}
          {
            <Button
              onClick={activeStep !== 4 ? onClickNext : onClickCreate}
              color="primary"
              variant="contained"
              disabled={!formValues.isFormValid}
              loading={isLoading}
            >
              {activeStep !== 4 ? t("common:continue") : t("button:createShare")}
            </Button>
          }
        </Stack>
      }
    >
      {activeStep !== 1 && formValues.stepOne.objectId && (
        <Stack mb={3}>
          <TitleCollectionCard
            poster={
              formValues.stepOne.objectLayer !== ObjectLayer.Group
                ? thumbnailService.GetObjectPosterUrl(
                    formValues.stepOne.objectLayer === ObjectLayer.Asset &&
                      formValues.stepOne.objectClass !== ObjectClass.Drive
                      ? formValues.stepOne.titleId!
                      : formValues.stepOne.objectId,
                    ThumbnailSize.small
                  )
                : undefined
            }
            title={formValues.stepOne.objectName || ""}
            desc={
              formValues.stepTwo.feature?.name
                ? formValues.stepTwo.feature?.name
                : formValues.assetIds.length > 0
                ? t("modal:body.contents", { count: formValues.assetIds.length })
                : ""
            }
            onClickAdd={() => false}
            onClickRemove={() => false}
            onClickEdit={() => {
              setActiveStep(startingStep);
            }}
            status="share"
          />
        </Stack>
      )}
      <Stack>
        {activeStep === 1 && <ModalCreateShareStepOne formValues={formValues} setFormValues={setFormValues} />}
        {activeStep === 2 && <ModalCreateShareStepTwo formValues={formValues} setFormValues={setFormValues} />}
        {activeStep === 3 && (
          <ModalCreateShareStepThree
            isFormValid={formValues.isFormValid}
            objectLayer={formValues.stepOne.objectLayer}
            securitySection={securitySection}
            setSecuritySection={setSecuritySection}
            settingsSection={settingsSection}
            setSettingsSection={setSettingsSection}
            localizationSection={localizationSection}
            setLocalizationSection={setLocalizationSection}
            recipientsSection={recipientsSection}
            setRecipientsSection={setRecipientsSection}
            setFormValues={setFormValues}
            setSecurityText={setSecurityText}
            securityText={securityText}
            isMultiSelect={props.assetIds ? true : false}
            isNameMandatory={props.isIndependentItems}
            title={props.object ?? formValues.stepOne.objectInfos ?? undefined}
          />
        )}
        {activeStep === 4 && <ModalCreateShareStepFour formValues={formValues} securityText={securityText} />}
      </Stack>
    </Modal>
  );
});

export default ModalCreateShare;
